import { useState, type ReactNode } from 'react';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BrainIcon,
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClipboardPlusIcon,
  ClockIcon,
  EuroIcon,
  EyeOffIcon,
  FlagIcon,
  HomeIcon,
  HourglassIcon,
  InfoIcon,
  ListIcon,
  PlusIcon,
  SearchIcon,
  SquareArrowOutUpRightIcon,
  StethoscopeIcon,
  TriangleAlertIcon,
  UserIcon,
  UserRoundIcon,
  UsersIcon,
  XIcon,
} from 'lucide-react';

/**
 * Nabootsing van het Ksyos-zorgverlenerportaal (connect.ksyos.com) voor de
 * sales-etalage op /e-health. Bedoeld om aan de directie van Ksyos te laten
 * zien hoe hun eigen omgeving binnen de SUPPOHRT-schil draait.
 *
 * De schermen zijn nagebouwd naar de screenshots in
 * docs/designs/screenshots-ksyos/. Kleuren zijn uit die PNG's gesampled, niet
 * geschat; zie KS hieronder. Die map staat NIET in de repo: er staan
 * patiëntgegevens op.
 *
 * Alle data is fictief en hardcoded, er zijn geen netwerkcalls. Twee dingen
 * zijn bewust aangevuld omdat ze buiten beeld vielen op de screenshots en de
 * demo anders doodloopt: de voettekst van de programma-modal (knop
 * 'Toevoegen') en de schermen achter 'Taken' en 'Patienten'.
 */

/** Uit de screenshots gesamplede Ksyos-huisstijl. */
const KS = {
  pink: '#e2176c',
  pinkTint: '#fce9f1',
  pinkSoft: '#fadae7',
  blue: '#0037cf',
  ink: '#463d37',
  body: '#2c2521',
  muted: '#6e6a66',
  bg: '#f9f8f7',
  panel: '#f2f2f0',
  line: '#e9e9e2',
  border: '#d8d4d2',
  inputBorder: '#bdb8b4',
  warnBg: '#ffe4bf',
  warnInk: '#ae2e12',
  orange: '#ff8f24',
  avatar: '#fddb43',
  avatarInk: '#7a4413',
};

const RAND_GRADIENT = 'linear-gradient(100deg, #fb782d 0%, #ec354f 48%, #e2176b 100%)';

type Nav = 'home' | 'taken' | 'patienten';
type Dienst = 'onderzoek' | 'begeleiding' | 'consult' | 'verwijzing';
type Zorgpad = 'kies-onderzoek' | 'aanvraag' | 'therapieland' | 'overzicht';

interface Patient {
  id: string;
  naam: string;
  geslacht: string;
  gebdatum: string;
  leeftijd: number;
  pid: string;
  /** 9 cijfers, links uitgelijnd in de lijst — zie overzicht-patienten-ksyos.png. */
  bsn: string;
  /** 10 cijfers, links uitgelijnd. */
  polis: string;
  email: string;
}

// Demopatienten. Namen volgen de vorm uit de screenshots (initialen, tussenvoegsel,
// achternaam) zodat de kolommen even breed vullen, maar zijn onmiskenbaar fictief.
// BSN's beginnen met 99999 en polisnummers met 46314 zodat ze nooit voor echt
// doorgaan.
const PATIENTEN: Patient[] = [
  {
    id: 'proef',
    naam: 'G. A. M. Proefpatiënt',
    geslacht: 'Man',
    gebdatum: '20-7-1954',
    leeftijd: 72,
    pid: '1000001',
    bsn: '999990011',
    polis: '4631400121',
    email: 'g.proefpatient@demo.nl',
  },
  {
    id: 'voorbeeld',
    naam: 'S. van der Voorbeeld',
    geslacht: 'Vrouw',
    gebdatum: '5-1-2002',
    leeftijd: 24,
    pid: '1000002',
    bsn: '999990022',
    polis: '4631400235',
    email: 's.vandervoorbeeld@demo.nl',
  },
  {
    id: 'demo',
    naam: 'D. van der Demo',
    geslacht: 'Man',
    gebdatum: '5-1-1990',
    leeftijd: 36,
    pid: '1000003',
    bsn: '999990033',
    polis: '4631400351',
    email: 'd.vanderdemo@demo.nl',
  },
  {
    id: 'test',
    naam: 'B. Testpersoon',
    geslacht: 'Vrouw',
    gebdatum: '14-6-1949',
    leeftijd: 77,
    pid: '1000004',
    bsn: '999990044',
    polis: '4631400478',
    email: 'b.testpersoon@demo.nl',
  },
  {
    id: 'kees',
    naam: 'K. Demopatiënt',
    geslacht: 'Man',
    gebdatum: '26-3-2003',
    leeftijd: 23,
    pid: '1000005',
    bsn: '999990055',
    polis: '4631400592',
    email: 'k.demopatient@demo.nl',
  },
];

const DIENSTEN: { key: Dienst; label: string }[] = [
  { key: 'onderzoek', label: 'Onderzoek' },
  { key: 'begeleiding', label: 'Begeleiding' },
  { key: 'consult', label: 'Consult' },
  { key: 'verwijzing', label: 'Verwijzing' },
];

const VERVOLGSTAPPEN = [
  'De patiënt ontvangt een e-mail met een link naar de vragenlijst van Embloom',
  'De patiënt vult de vragenlijst in',
  'Je ziet de antwoorden in het Ksyos platform',
  'Je beoordeelt zelf de vragenlijst',
  'Je ontvangt terugkoppeling via Edifact in je HIS als je je ZorgMail nummer hebt doorgegeven',
];

// Alfabetische programmalijst zoals die in de Therapieland-modal staat; de
// modal vult twee kolommen van boven naar beneden.
const PROGRAMMAS = [
  '(Geen) Zin in seks',
  'Aanhoudende (pijn)klachten - Mind-body benadering',
  'Aanhoudende lichamelijke klachten (ALK) - Ouders',
  'Aanhoudende lichamelijke klachten (ALK) - Jongeren',
  'Accepteren',
  'ACT',
  'ACT - Acceptatie',
  'ACT - Defusie',
  'ACT - Jezelf observeren',
  'ACT - Mindfulness',
  'ACT - Toegewijde actie',
  'ACT - van Klacht naar Veerkracht',
  'ACT - Waarden',
  'ACT your way - Jongvolwassenen',
  'Activatie',
  'AD(H)D - Ouders',
  'ADHD',
  'ADHD-medicatie (psycho-educatie)',
  'Afbouwen antidepressiva',
  'Je slaap verbeteren',
  'Kanker - Na de behandeling',
  'Kanker - Na de diagnose',
  'Kanker - Tijdens de behandeling',
  'Kennis van gezondheid',
  'Kernkwadranten',
  'KOPP - Jongeren',
  'Leefstijl',
  'Leefstijl 60+',
  'Lifestyle',
  'Loneliness',
  'Managing your money',
  'Mantelzorg',
  'Mentale revalidatie na corona',
  'Mentale revalidatie na corona - Sociale omgeving',
  'Middelengebruik bij je naaste',
  'Minder Angst na Kanker',
  'Mindfulness',
  'Mindfulness - Jongeren',
];

const THEMAS = ['Stress', 'Overspanning & Burn-out', 'Zelfbeeld', 'Somberheid', 'Angst'];

export function KsyosEnv({ onNaarTherapieland }: { onNaarTherapieland: () => void }): JSX.Element {
  const [ingelogd, setIngelogd] = useState(false);
  const [nav, setNav] = useState<Nav>('home');
  const [dienst, setDienst] = useState<Dienst | null>(null);
  const [patient, setPatient] = useState<Patient | null>(null);
  const [zorgpad, setZorgpad] = useState<Zorgpad>('kies-onderzoek');
  const [verstuurd, setVerstuurd] = useState<'4dkl' | 'therapieland' | null>(null);

  if (!ingelogd) return <KsyosLogin onLogin={() => setIngelogd(true)} />;

  function openPatient(p: Patient, start: Zorgpad): void {
    setPatient(p);
    setZorgpad(start);
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col" style={{ backgroundColor: KS.bg }}>
      <KsyosTopBar
        nav={patient ? null : nav}
        onNav={(n) => {
          setPatient(null);
          setDienst(null);
          setNav(n);
        }}
        onNaarTherapieland={onNaarTherapieland}
      />

      <div className="relative min-h-0 flex-1 overflow-y-auto">
        {patient ? (
          <PatientScherm
            patient={patient}
            zorgpad={zorgpad}
            setZorgpad={setZorgpad}
            verstuurd={verstuurd}
            setVerstuurd={setVerstuurd}
            onTerug={() => setPatient(null)}
          />
        ) : nav === 'home' ? (
          <HomeScherm
            dienst={dienst}
            setDienst={setDienst}
            onPatient={(p) =>
              openPatient(p, dienst === 'begeleiding' ? 'therapieland' : 'kies-onderzoek')
            }
          />
        ) : nav === 'taken' ? (
          <TakenScherm onPatient={(p) => openPatient(p, 'overzicht')} />
        ) : (
          <PatientenScherm onPatient={(p) => openPatient(p, 'overzicht')} />
        )}
        <HulpTab />
      </div>
    </div>
  );
}

function KsyosTopBar({
  nav,
  onNav,
  onNaarTherapieland,
}: {
  nav: Nav | null;
  onNav: (n: Nav) => void;
  onNaarTherapieland: () => void;
}): JSX.Element {
  const items: { key: Nav; label: string; Icon: typeof HomeIcon }[] = [
    { key: 'home', label: 'Home', Icon: HomeIcon },
    { key: 'taken', label: 'Taken', Icon: FlagIcon },
    { key: 'patienten', label: 'Patiënten', Icon: UsersIcon },
  ];

  return (
    <div
      className="flex h-[68px] shrink-0 items-stretch justify-between bg-white pl-6 pr-7"
      style={{ borderBottom: `1px solid ${KS.line}` }}
    >
      <div className="flex items-stretch gap-9">
        <img src="/ksyos-logo.svg" alt="Ksyos" className="my-auto h-[30px] w-auto" />
        <nav className="flex items-stretch gap-7">
          {items.map(({ key, label, Icon }) => {
            const on = nav === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => onNav(key)}
                className="flex items-center gap-2 text-[15px]"
                style={{
                  color: on ? KS.pink : KS.body,
                  fontWeight: on ? 600 : 400,
                  boxShadow: on ? `inset 0 -3px 0 0 ${KS.pink}` : undefined,
                }}
              >
                <Icon size={17} /> {label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={onNaarTherapieland}
            className="flex items-center gap-2 text-[15px]"
            style={{ color: KS.body }}
          >
            <SquareArrowOutUpRightIcon size={16} /> Naar Therapieland
          </button>
        </nav>
      </div>

      <div className="flex items-center gap-3">
        <span
          className="flex h-[34px] w-[34px] items-center justify-center rounded-full text-[12.5px] font-bold"
          style={{ backgroundColor: KS.avatar, color: KS.avatarInk }}
        >
          TT
        </span>
        <span className="leading-tight">
          <span className="block text-[14px] font-bold" style={{ color: KS.body }}>
            T. Testarts
          </span>
          <span className="block text-[12.5px]" style={{ color: KS.muted }}>
            Huisartsenpraktijk Demo
          </span>
        </span>
        <ChevronDownIcon size={18} style={{ color: KS.muted }} />
      </div>
    </div>
  );
}

/** Oranje uitklaptab tegen de rechterrand, zoals in het echte portaal. */
function HulpTab(): JSX.Element {
  return (
    <span
      className="absolute right-0 top-1/2 -translate-y-1/2 rounded-l-md px-1.5 py-4 text-[12.5px] font-semibold text-white"
      style={{ backgroundColor: KS.orange, writingMode: 'vertical-rl' }}
    >
      Hulp nodig?
    </span>
  );
}

function Wrap({ children }: { children: ReactNode }): JSX.Element {
  return <div className="mx-auto w-full max-w-[1240px] px-10 py-8">{children}</div>;
}

/* ---------------------------------------------------------------- Home --- */

function HomeScherm({
  dienst,
  setDienst,
  onPatient,
}: {
  dienst: Dienst | null;
  setDienst: (d: Dienst | null) => void;
  onPatient: (p: Patient) => void;
}): JSX.Element {
  const [gezocht, setGezocht] = useState(false);

  return (
    <Wrap>
      <h1 className="text-[30px] font-bold" style={{ color: KS.ink }}>
        Hi Tessa, welkom bij Ksyos!
      </h1>

      {/* Verloopkader: buitenlaag draagt het verloop, binnenlaag dekt het af. */}
      <div className="mt-6 rounded-[13px] p-1" style={{ background: RAND_GRADIENT }}>
        <div className="relative rounded-[9px] bg-white px-10 py-8">
          <span
            className="absolute right-8 top-8 flex h-9 w-9 items-center justify-center rounded-full"
            style={{ backgroundColor: KS.panel, color: KS.ink }}
          >
            <XIcon size={17} />
          </span>

          <p className="text-[15px] font-bold" style={{ color: KS.ink }}>
            Start met
          </p>
          <span
            className="mt-3 inline-block rounded-lg px-5 py-2 text-[15px] font-bold text-white"
            style={{ backgroundColor: KS.pink }}
          >
            GGZ
          </span>

          <p className="mt-7 text-[15px] font-bold" style={{ color: KS.ink }}>
            Kies het type dienst
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            {DIENSTEN.map((d) => {
              const on = dienst === d.key;
              return (
                <button
                  key={d.key}
                  type="button"
                  onClick={() => {
                    setDienst(on ? null : d.key);
                    setGezocht(false);
                  }}
                  className="rounded-lg px-[18px] py-2 text-[15px] font-bold transition-colors"
                  style={{
                    border: `1.5px solid ${KS.pink}`,
                    backgroundColor: on ? KS.pink : '#fff',
                    color: on ? '#fff' : KS.pink,
                  }}
                >
                  {d.label}
                </button>
              );
            })}
          </div>

          {dienst === 'begeleiding' && (
            <>
              <p className="mt-7 text-[15px] font-bold" style={{ color: KS.ink }}>
                Kies het type begeleiding dat je wilt starten
              </p>
              <div className="mt-3">
                <DienstKaart
                  titel="Therapieland"
                  intro="Voor het begeleiden van de patiënt met psychische klachten met behulp van e-Health programma's"
                  stappen={[
                    "Verstuur één of meerdere e-Health programma's",
                    'De patiënt maakt een account en start direct',
                    'De voortgang kan worden gemonitord. De patiënt kan beveiligd berichten sturen.',
                  ]}
                  doorlooptijd="verschilt per e-Health programma, patiënt en soort klacht"
                />
              </div>
            </>
          )}

          {dienst === 'onderzoek' && (
            <>
              <p className="mt-7 text-[15px] font-bold" style={{ color: KS.ink }}>
                Kies het type onderzoek dat je wilt starten
              </p>
              <div className="mt-3">
                <DienstKaart
                  titel="GGZ Embloom"
                  intro="Voor het in beeld brengen van klachten"
                  stappen={[
                    'Verstuur een digitale vragenlijst naar de patiënt',
                    'De vragenlijst geeft inzicht in de (ernst van de) klachten',
                    'Bekijk de score (en het advies) van de vragenlijst en sluit het proces af',
                    'Je ontvangt de uitslag via Edifact (in je HIS)',
                  ]}
                  doorlooptijd="1 dag"
                />
              </div>
            </>
          )}

          {(dienst === 'consult' || dienst === 'verwijzing') && (
            <p className="mt-7 text-[15px]" style={{ color: KS.muted }}>
              In deze demo zijn alleen Onderzoek en Begeleiding uitgewerkt.
            </p>
          )}

          {(dienst === 'onderzoek' || dienst === 'begeleiding') && (
            <>
              <p className="mt-8 text-[15px] font-bold" style={{ color: KS.ink }}>
                Voor welke patiënt?
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-3">
                <Zoekveld label="Naam" breed="w-[230px]" />
                <Zoekveld label="BSN" breed="w-[230px]" />
                <label className="block">
                  <span className="block text-[14px]" style={{ color: KS.body }}>
                    Geboortedatum
                  </span>
                  <span
                    className="mt-1.5 flex w-[165px] items-center justify-between rounded border bg-white px-3 py-2.5 text-[14px]"
                    style={{ borderColor: KS.inputBorder, color: KS.muted }}
                  >
                    dd-mm-jjjj <CalendarIcon size={16} style={{ color: KS.pink }} />
                  </span>
                </label>
                <Zoekveld label="Procesnummer" breed="w-[230px]" />
                <button
                  type="button"
                  onClick={() => setGezocht(true)}
                  className="rounded-lg px-[18px] py-2.5 text-[15px] font-bold text-white"
                  style={{ backgroundColor: KS.pink }}
                >
                  Zoek
                </button>
                <span
                  className="flex items-center gap-1.5 pb-1 text-[15px] font-bold"
                  style={{ color: KS.pink }}
                >
                  <PlusIcon size={17} /> Nieuwe patiënt
                </span>
              </div>

              {gezocht && (
                <div
                  className="mt-5 overflow-hidden rounded"
                  style={{ border: `1px solid ${KS.border}` }}
                >
                  {PATIENTEN.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => onPatient(p)}
                      className="flex w-full items-center justify-between px-5 py-3 text-left hover:bg-[#faf6f8]"
                      style={{ borderTop: `1px solid ${KS.line}`, color: KS.body }}
                    >
                      <span className="text-[14.5px] font-bold" style={{ color: KS.pink }}>
                        {p.naam}
                      </span>
                      <span className="text-[13.5px]" style={{ color: KS.muted }}>
                        {p.geslacht} {p.gebdatum} ({p.leeftijd} jaar) · PID {p.pid}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <hr className="mt-10" style={{ borderColor: KS.line }} />

      <div className="mt-7 flex gap-6">
        {/* De echte pagina toont hier een foto van de servicedeskmedewerker. */}
        <span
          className="flex h-[76px] w-[76px] shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: KS.panel, color: KS.muted }}
        >
          <UserRoundIcon size={34} />
        </span>
        <div className="text-[15px]" style={{ color: KS.body }}>
          <p className="font-bold" style={{ color: KS.ink }}>
            Hulp nodig?
          </p>
          <p className="mt-0.5">
            Onze service desk staat voor je klaar op werkdagen tussen 8:30 en 17:00.
          </p>
          <p className="mt-3">
            Stel je vraag via ons{' '}
            <span className="font-bold underline" style={{ color: KS.pink }}>
              contactformulier
            </span>
            .
          </p>
        </div>
      </div>
    </Wrap>
  );
}

function Zoekveld({ label, breed }: { label: string; breed: string }): JSX.Element {
  return (
    <label className="block">
      <span className="block text-[14px]" style={{ color: KS.body }}>
        {label}
      </span>
      <input
        type="text"
        className={`mt-1.5 ${breed} rounded border bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[color:var(--brand)]`}
        style={{ borderColor: KS.inputBorder, color: KS.body }}
      />
    </label>
  );
}

function DienstKaart({
  titel,
  intro,
  stappen,
  doorlooptijd,
  onStart,
}: {
  titel: string;
  intro: string;
  stappen: string[];
  doorlooptijd: string;
  onStart?: () => void;
}): JSX.Element {
  return (
    <div
      className="w-[372px] rounded px-5 py-4"
      style={{ border: `1px solid ${onStart ? KS.border : KS.pink}` }}
    >
      <p className="text-[15px] font-bold" style={{ color: KS.ink }}>
        {titel}
      </p>
      <p className="mt-2 text-[13.5px] leading-snug" style={{ color: KS.body }}>
        {intro}
      </p>
      <ol className="mt-3 space-y-1.5 text-[13.5px] leading-snug" style={{ color: KS.body }}>
        {stappen.map((s, i) => (
          <li key={s} className="flex gap-2">
            <span className="shrink-0" style={{ color: KS.body }}>
              {i + 1}.
            </span>
            <span>{s}</span>
          </li>
        ))}
      </ol>
      <p className="mt-4 flex gap-2 text-[13.5px] leading-snug" style={{ color: KS.muted }}>
        <ClockIcon size={15} className="mt-0.5 shrink-0" /> Doorlooptijd: {doorlooptijd}
      </p>
      <p className="mt-1 flex gap-2 text-[13.5px]" style={{ color: KS.muted }}>
        <EuroIcon size={15} className="mt-0.5 shrink-0" /> Kosten: geen
      </p>
      {onStart && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={onStart}
            className="rounded-lg px-6 py-2 text-[15px] font-bold text-white"
            style={{ backgroundColor: KS.pink }}
          >
            Start
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Patiënt --- */

function PatientScherm({
  patient,
  zorgpad,
  setZorgpad,
  verstuurd,
  setVerstuurd,
  onTerug,
}: {
  patient: Patient;
  zorgpad: Zorgpad;
  setZorgpad: (z: Zorgpad) => void;
  verstuurd: '4dkl' | 'therapieland' | null;
  setVerstuurd: (v: '4dkl' | 'therapieland' | null) => void;
  onTerug: () => void;
}): JSX.Element {
  const [tab, setTab] = useState<'zorgpad' | 'contact' | 'protocol'>('zorgpad');
  const tabs = [
    { key: 'zorgpad' as const, label: 'Zorgpad' },
    { key: 'contact' as const, label: 'Contact Ksyos' },
    { key: 'protocol' as const, label: 'Medisch protocol' },
  ];

  return (
    <div className="flex min-h-full">
      <PatientSidebar patient={patient} onTerug={onTerug} />

      <div className="min-w-0 flex-1 px-12 py-8">
        <div className="flex items-center gap-4">
          <BrainIcon size={38} strokeWidth={1.6} style={{ color: KS.pink }} />
          <h1 className="text-[32px] font-bold" style={{ color: KS.ink }}>
            GGZ
          </h1>
        </div>

        <div className="mt-5 flex gap-8" style={{ borderBottom: `1px solid ${KS.line}` }}>
          {tabs.map((t) => {
            const on = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="pb-2.5 text-[16px]"
                style={{
                  color: on ? KS.pink : KS.body,
                  boxShadow: on ? `inset 0 -2px 0 0 ${KS.pink}` : undefined,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {tab !== 'zorgpad' ? (
          <p className="mt-8 text-[15px]" style={{ color: KS.muted }}>
            In deze demo is alleen het tabblad Zorgpad uitgewerkt.
          </p>
        ) : zorgpad === 'kies-onderzoek' ? (
          <KiesOnderzoek onAanvraag={() => setZorgpad('aanvraag')} />
        ) : zorgpad === 'aanvraag' ? (
          <Aanvraag4dkl
            patient={patient}
            onAnnuleer={() => setZorgpad('kies-onderzoek')}
            onVerstuur={() => {
              setVerstuurd('4dkl');
              setZorgpad('overzicht');
            }}
          />
        ) : zorgpad === 'therapieland' ? (
          <TherapielandAanvraag
            patient={patient}
            onAnnuleer={onTerug}
            onVerstuur={() => {
              setVerstuurd('therapieland');
              setZorgpad('overzicht');
            }}
          />
        ) : (
          <ZorgpadOverzicht
            patient={patient}
            soort={verstuurd ?? '4dkl'}
            onNieuw={() => setZorgpad('kies-onderzoek')}
          />
        )}
      </div>
    </div>
  );
}

function PatientSidebar({
  patient,
  onTerug,
}: {
  patient: Patient;
  onTerug: () => void;
}): JSX.Element {
  const [huisartsOpen, setHuisartsOpen] = useState(true);
  const [gegevensOpen, setGegevensOpen] = useState(false);

  return (
    <aside className="w-[320px] shrink-0 px-5 py-8" style={{ backgroundColor: KS.panel }}>
      <span
        className="mx-auto flex h-[82px] w-[82px] items-center justify-center rounded-full bg-white"
        style={{ color: KS.muted }}
      >
        <UserIcon size={38} strokeWidth={1.4} />
      </span>
      <button
        type="button"
        onClick={onTerug}
        className="mt-3 block w-full text-center text-[16px] underline"
        style={{ color: KS.body }}
      >
        {patient.naam}
      </button>
      <p className="text-center text-[15px]" style={{ color: KS.body }}>
        {patient.geslacht} {patient.gebdatum} ({patient.leeftijd} jaar)
      </p>

      <button
        type="button"
        onClick={() => setHuisartsOpen(!huisartsOpen)}
        className="mt-8 flex w-full items-center justify-between"
      >
        <span className="flex items-center gap-2.5 text-[16px] font-bold" style={{ color: KS.ink }}>
          <UserIcon size={17} style={{ color: KS.pink }} /> Huisarts
        </span>
        {huisartsOpen ? (
          <ChevronUpIcon size={18} style={{ color: KS.ink }} />
        ) : (
          <ChevronDownIcon size={18} style={{ color: KS.ink }} />
        )}
      </button>

      {huisartsOpen && (
        <div className="mt-4 pl-7">
          {[
            ['Naam', 'D. Demo'],
            ['Huisartsenpraktijk', 'Huisartsenpraktijk Demo'],
            ['Praktijknummer', '0000 000000'],
          ].map(([label, waarde]) => (
            <div key={label} className="mb-3">
              <p className="text-[12.5px] font-bold" style={{ color: KS.body }}>
                {label}
              </p>
              <p className="text-[14px]" style={{ color: KS.body }}>
                {waarde}
              </p>
            </div>
          ))}
          <span className="text-[14px] font-bold underline" style={{ color: KS.pink }}>
            Wijzig huisarts
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={() => setGegevensOpen(!gegevensOpen)}
        className="mt-7 flex w-full items-center justify-between rounded-lg px-3 py-3"
        style={
          gegevensOpen ? { border: `2px solid ${KS.blue}`, backgroundColor: '#fff' } : undefined
        }
      >
        <span className="flex items-center gap-2.5 text-[16px] font-bold" style={{ color: KS.ink }}>
          <UserIcon size={17} style={{ color: KS.pink }} /> Patiëntgegevens
        </span>
        {gegevensOpen ? (
          <ChevronUpIcon size={18} style={{ color: KS.ink }} />
        ) : (
          <ChevronDownIcon size={18} style={{ color: KS.ink }} />
        )}
      </button>
      {gegevensOpen && (
        <div className="mt-3 pl-3">
          {[
            ['E-mailadres', patient.email],
            ['PID', patient.pid],
          ].map(([label, waarde]) => (
            <div key={label} className="mb-3">
              <p className="text-[12.5px] font-bold" style={{ color: KS.body }}>
                {label}
              </p>
              <p className="text-[14px]" style={{ color: KS.body }}>
                {waarde}
              </p>
            </div>
          ))}
        </div>
      )}
    </aside>
  );
}

/* ---------------------------------------------------- Onderzoek starten --- */

function KiesOnderzoek({ onAanvraag }: { onAanvraag: () => void }): JSX.Element {
  const [open, setOpen] = useState<'beslis' | 'vragenlijst' | null>('beslis');

  return (
    <div className="mt-6 rounded bg-white px-8 py-7" style={{ border: `1px solid ${KS.line}` }}>
      <p className="text-[17px] font-bold" style={{ color: KS.ink }}>
        Kies het type onderzoek dat je wilt starten
      </p>

      <div className="mt-4">
        <DienstKaart
          titel="GGZ Embloom"
          intro="Voor het in beeld brengen van klachten"
          stappen={[
            'Verstuur een digitale vragenlijst naar de patiënt',
            'De vragenlijst geeft inzicht in de (ernst van de) klachten',
            'Bekijk de score (en het advies) van de vragenlijst en sluit het proces af',
            'Je ontvangt de uitslag via Edifact (in je HIS)',
          ]}
          doorlooptijd="1 dag"
          onStart={onAanvraag}
        />
      </div>

      <div className="relative mt-6 flex gap-3">
        {(['beslis', 'vragenlijst'] as const).map((k) => {
          const on = open === k;
          const label = k === 'beslis' ? 'Beslisondersteuner' : 'Vragenlijst';
          return (
            <button
              key={k}
              type="button"
              onClick={() => setOpen(on ? null : k)}
              className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-[15px] font-bold"
              style={{
                border: `1.5px solid ${KS.pink}`,
                outline: on ? `2px solid ${KS.pink}` : undefined,
                outlineOffset: on ? '2px' : undefined,
                backgroundColor: on ? KS.pink : '#fff',
                color: on ? '#fff' : KS.pink,
              }}
            >
              {label} <ChevronDownIcon size={17} />
            </button>
          );
        })}
      </div>

      {open && (
        <div
          className="relative mt-3 w-[386px] rounded px-5 py-5"
          style={{ border: `1.5px solid ${KS.pink}` }}
        >
          <span
            className="absolute -top-[9px] h-4 w-4 rotate-45 bg-white"
            style={{
              left: open === 'beslis' ? 96 : 300,
              borderTop: `1.5px solid ${KS.pink}`,
              borderLeft: `1.5px solid ${KS.pink}`,
            }}
          />
          <span
            className="flex items-center justify-between rounded border px-3 py-2.5 text-[14.5px]"
            style={{ borderColor: KS.inputBorder, color: KS.muted }}
          >
            {open === 'beslis' ? 'Zoek een vragenlijst' : 'Zoek een vragenlijst'}
            <SearchIcon size={17} style={{ color: KS.body }} />
          </span>
          <p className="mt-4 text-[14.5px]" style={{ color: KS.body }}>
            Of kies uit de lijst
          </p>
          <button
            type="button"
            onClick={onAanvraag}
            className="mt-3 w-full rounded px-4 py-3 text-left hover:bg-[#faf6f8]"
            style={{ border: `1px solid ${KS.border}` }}
          >
            <span className="block text-[15px] font-bold" style={{ color: KS.ink }}>
              4DKL+
            </span>
            <span className="mt-1 block text-[13.5px] leading-snug" style={{ color: KS.body }}>
              Screening syndroomstoornissen, somatische klachten en psychosociale en
              omgevingsfactoren
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------- 4DKL+ aanvraag --- */

function Aanvraag4dkl({
  patient,
  onAnnuleer,
  onVerstuur,
}: {
  patient: Patient;
  onAnnuleer: () => void;
  onVerstuur: () => void;
}): JSX.Element {
  const [nameting, setNameting] = useState(false);

  return (
    <div className="mt-6 rounded bg-white px-8 py-7" style={{ border: `1px solid ${KS.line}` }}>
      <div className="flex items-center gap-3">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-full"
          style={{ backgroundColor: '#eef1fb', color: KS.blue }}
        >
          <ClipboardPlusIcon size={19} />
        </span>
        <h2 className="text-[19px] font-bold" style={{ color: KS.ink }}>
          4DKL+ aanvraag
        </h2>
        <InfoIcon size={17} style={{ color: KS.blue }} />
      </div>

      <div className="mt-6 space-y-5">
        <FormRij label="Huisarts" verplicht>
          <span
            className="flex items-center justify-between rounded border bg-white px-3 py-2.5 text-[14.5px]"
            style={{ borderColor: KS.inputBorder, color: KS.body }}
          >
            D. Demo <ChevronDownIcon size={17} />
          </span>
        </FormRij>

        <div
          className="flex items-center gap-3 rounded px-5 py-4"
          style={{ backgroundColor: KS.warnBg }}
        >
          <TriangleAlertIcon size={19} style={{ color: KS.warnInk }} />
          <span className="text-[15px] font-bold" style={{ color: KS.warnInk }}>
            Controleer of het e-mailadres van de patiënt juist is.
          </span>
        </div>

        <FormRij label="E-mailadres" verplicht>
          <input
            type="email"
            defaultValue={patient.email}
            className="w-full rounded border bg-white px-3 py-2.5 text-[14.5px] outline-none focus:border-[color:var(--brand)]"
            style={{ borderColor: KS.inputBorder, color: KS.body }}
          />
        </FormRij>

        <FormRij label="Selecteer een beoordelaar" verplicht>
          <span
            className="flex items-center justify-between px-1 py-2 text-[14.5px] font-bold"
            style={{ borderBottom: `1px solid ${KS.inputBorder}`, color: KS.body }}
          >
            T. Testarts <ChevronDownIcon size={17} style={{ color: KS.muted }} />
          </span>
        </FormRij>

        <FormRij label="Nameting sturen">
          <span className="flex items-center gap-3 text-[15px]" style={{ color: KS.body }}>
            <button
              type="button"
              onClick={() => setNameting(!nameting)}
              className="flex h-[18px] w-[18px] items-center justify-center rounded-sm"
              style={{
                border: `1.5px solid ${nameting ? KS.pink : KS.inputBorder}`,
                backgroundColor: nameting ? KS.pink : '#fff',
              }}
            >
              {nameting && <span className="text-[11px] font-bold text-white">✓</span>}
            </button>
            Ja, na
            <input
              type="text"
              className="w-[60px] px-1 py-1 text-center text-[15px] outline-none"
              style={{ borderBottom: `1px solid ${KS.inputBorder}`, color: KS.body }}
            />
            weken
          </span>
        </FormRij>
      </div>

      <Vervolgstappen />

      <div className="mt-7 flex justify-end gap-4">
        <BlauweKnop onClick={onAnnuleer}>Annuleer</BlauweKnop>
        <BlauweKnop onClick={onVerstuur}>Bewaar als concept</BlauweKnop>
        <BlauweKnop onClick={onVerstuur} gevuld>
          Verstuur
        </BlauweKnop>
      </div>
    </div>
  );
}

function FormRij({
  label,
  verplicht,
  children,
}: {
  label: string;
  verplicht?: boolean;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-4">
      <span className="w-[260px] shrink-0 text-[15px]" style={{ color: KS.body }}>
        {label}
        {verplicht && <span style={{ color: KS.pink }}> *</span>}
      </span>
      <span className="min-w-[280px] flex-1">{children}</span>
    </div>
  );
}

function BlauweKnop({
  children,
  onClick,
  gevuld,
}: {
  children: ReactNode;
  onClick: () => void;
  gevuld?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md px-6 py-2.5 text-[15px] font-bold"
      style={{
        border: `1.5px solid ${KS.blue}`,
        backgroundColor: gevuld ? KS.blue : '#fff',
        color: gevuld ? '#fff' : KS.blue,
      }}
    >
      {children}
    </button>
  );
}

function Vervolgstappen(): JSX.Element {
  return (
    <>
      <p className="mt-8 text-[16px] font-bold" style={{ color: KS.ink }}>
        Vervolgstappen onderzoek Embloom
      </p>
      <ol className="mt-4">
        {VERVOLGSTAPPEN.map((stap, i) => (
          <li key={stap} className="flex gap-4">
            <span className="flex w-5 shrink-0 flex-col items-center">
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{ border: `2px solid ${KS.pink}` }}
              >
                <span
                  className="h-[7px] w-[7px] rounded-full"
                  style={{ backgroundColor: KS.pink }}
                />
              </span>
              {i < VERVOLGSTAPPEN.length - 1 && (
                <span className="w-px flex-1" style={{ borderLeft: `1px dotted ${KS.muted}` }} />
              )}
            </span>
            <span className="pb-4 text-[15px] leading-snug" style={{ color: KS.body }}>
              {i === VERVOLGSTAPPEN.length - 1 ? (
                <>
                  Je ontvangt terugkoppeling via Edifact in je HIS als je je{' '}
                  <span className="font-bold underline" style={{ color: KS.blue }}>
                    ZorgMail nummer hebt doorgegeven
                  </span>
                </>
              ) : (
                stap
              )}
            </span>
          </li>
        ))}
      </ol>
    </>
  );
}

/* ------------------------------------------- Therapieland-aanvraag + modal --- */

function TherapielandAanvraag({
  patient,
  onAnnuleer,
  onVerstuur,
}: {
  patient: Patient;
  onAnnuleer: () => void;
  onVerstuur: () => void;
}): JSX.Element {
  const [modal, setModal] = useState(false);
  const [gekozen, setGekozen] = useState<string[]>([]);

  return (
    <div className="mt-6 rounded bg-white px-8 py-7" style={{ border: `1px solid ${KS.line}` }}>
      <div className="flex items-center gap-3">
        <span
          className="flex h-9 w-9 items-center justify-center rounded-full"
          style={{ backgroundColor: '#eef1fb', color: KS.blue }}
        >
          <ClipboardPlusIcon size={19} />
        </span>
        <h2 className="text-[19px] font-bold" style={{ color: KS.ink }}>
          Therapieland aanvraag
        </h2>
        <InfoIcon size={17} style={{ color: KS.blue }} />
      </div>

      <div className="mt-6 space-y-5">
        <FormRij label="Huisarts" verplicht>
          <span
            className="flex items-center justify-between rounded border bg-white px-3 py-2.5 text-[14.5px]"
            style={{ borderColor: KS.inputBorder, color: KS.body }}
          >
            D. Demo <ChevronDownIcon size={17} />
          </span>
        </FormRij>

        <div
          className="flex items-center gap-3 rounded px-5 py-4"
          style={{ backgroundColor: KS.warnBg }}
        >
          <TriangleAlertIcon size={19} style={{ color: KS.warnInk }} />
          <span className="text-[15px] font-bold" style={{ color: KS.warnInk }}>
            Controleer of het e-mailadres van de patiënt juist is.
          </span>
        </div>

        <FormRij label="E-mailadres" verplicht>
          <input
            type="email"
            defaultValue={patient.email}
            className="w-full rounded border bg-white px-3 py-2.5 text-[14.5px] outline-none focus:border-[color:var(--brand)]"
            style={{ borderColor: KS.inputBorder, color: KS.body }}
          />
        </FormRij>

        <FormRij label="e-Health programma's" verplicht>
          <button
            type="button"
            onClick={() => setModal(true)}
            className="flex w-full items-center justify-between rounded border bg-white px-3 py-2.5 text-left text-[14.5px]"
            style={{ borderColor: KS.inputBorder, color: gekozen.length ? KS.body : KS.muted }}
          >
            {gekozen.length ? `${gekozen.length} programma('s) gekozen` : 'Kies programma’s'}
            <ChevronDownIcon size={17} />
          </button>
          {gekozen.length > 0 && (
            <span className="mt-2 flex flex-wrap gap-2">
              {gekozen.map((g) => (
                <span
                  key={g}
                  className="rounded-full px-3 py-1 text-[12.5px] font-bold"
                  style={{ backgroundColor: KS.pinkTint, color: KS.pink }}
                >
                  {g}
                </span>
              ))}
            </span>
          )}
        </FormRij>

        <FormRij label="Bericht aan de patiënt">
          <textarea
            rows={3}
            defaultValue="Beste, hierbij ontvang je toegang tot het e-Health programma dat we samen hebben besproken."
            className="w-full rounded border bg-white px-3 py-2.5 text-[14.5px] outline-none focus:border-[color:var(--brand)]"
            style={{ borderColor: KS.inputBorder, color: KS.body }}
          />
        </FormRij>
      </div>

      <div className="mt-7 flex justify-end gap-4">
        <BlauweKnop onClick={onAnnuleer}>Annuleer</BlauweKnop>
        <BlauweKnop onClick={onVerstuur}>Bewaar als concept</BlauweKnop>
        <BlauweKnop onClick={onVerstuur} gevuld>
          Verstuur
        </BlauweKnop>
      </div>

      {modal && (
        <ProgrammaModal gekozen={gekozen} setGekozen={setGekozen} onSluit={() => setModal(false)} />
      )}
    </div>
  );
}

function ProgrammaModal({
  gekozen,
  setGekozen,
  onSluit,
}: {
  gekozen: string[];
  setGekozen: (g: string[]) => void;
  onSluit: () => void;
}): JSX.Element {
  const [zoek, setZoek] = useState('');
  const [thema, setThema] = useState<string | null>(null);
  const lijst = PROGRAMMAS.filter((p) => p.toLowerCase().includes(zoek.toLowerCase()));

  function toggle(p: string): void {
    setGekozen(gekozen.includes(p) ? gekozen.filter((g) => g !== p) : [...gekozen, p]);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-6">
      <div className="flex max-h-full w-full max-w-[900px] flex-col rounded-xl bg-white">
        <div className="relative px-10 pt-9">
          <button
            type="button"
            onClick={onSluit}
            className="absolute right-8 top-7 flex h-10 w-10 items-center justify-center rounded-full"
            style={{ border: `1.5px solid ${KS.blue}`, color: KS.ink }}
          >
            <XIcon size={20} />
          </button>

          <h2 className="text-[26px] font-bold" style={{ color: KS.ink }}>
            Therapieland programma&apos;s
          </h2>

          <div className="mt-5 flex flex-wrap items-end gap-8">
            <input
              type="text"
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              placeholder="Start met typen om het juiste programma te vinden"
              className="w-[400px] bg-transparent px-1 py-1.5 text-[15px] outline-none"
              style={{ borderBottom: `1px solid ${KS.inputBorder}`, color: KS.body }}
            />
            <span
              className="flex w-[190px] items-center justify-between px-1 py-1.5 text-[15px]"
              style={{ borderBottom: `1px solid ${KS.inputBorder}`, color: KS.body }}
            >
              Volwassenen <ChevronDownIcon size={17} />
            </span>
            <span
              className="flex items-center gap-2 pb-1.5 text-[15px] font-bold"
              style={{ color: KS.blue }}
            >
              <ListIcon size={17} /> Inhoudsopgave
            </span>
          </div>

          <p className="mt-6 flex items-center gap-2 text-[14.5px]" style={{ color: KS.body }}>
            Thema&apos;s <InfoIcon size={15} style={{ color: KS.blue }} />
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            {THEMAS.map((t) => {
              const on = thema === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setThema(on ? null : t)}
                  className="rounded-full px-5 py-2 text-[15px] font-bold"
                  style={{
                    border: `1.5px solid ${KS.blue}`,
                    backgroundColor: on ? KS.blue : '#fff',
                    color: on ? '#fff' : KS.blue,
                  }}
                >
                  {t}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-10 py-6">
          <div className="columns-2 gap-10">
            {lijst.map((p) => {
              const on = gekozen.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => toggle(p)}
                  className="mb-3 flex w-full break-inside-avoid items-start gap-3 text-left"
                >
                  <span
                    className="mt-0.5 flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-sm"
                    style={{
                      border: `1.5px solid ${on ? KS.blue : KS.inputBorder}`,
                      backgroundColor: on ? KS.blue : '#fff',
                    }}
                  >
                    {on && <span className="text-[12px] font-bold text-white">✓</span>}
                  </span>
                  <span className="text-[15px] leading-snug" style={{ color: KS.body }}>
                    {p}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Voettekst viel buiten beeld op de screenshot; nagebouwd zodat de
            demo terug kan naar het aanvraagformulier. */}
        <div
          className="flex items-center justify-between px-10 py-5"
          style={{ borderTop: `1px solid ${KS.line}` }}
        >
          <span className="text-[14.5px]" style={{ color: KS.muted }}>
            {gekozen.length} programma&apos;s geselecteerd
          </span>
          <BlauweKnop onClick={onSluit} gevuld>
            Toevoegen
          </BlauweKnop>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------- Zorgpad-overzicht --- */

function ZorgpadOverzicht({
  patient,
  soort,
  onNieuw,
}: {
  patient: Patient;
  soort: '4dkl' | 'therapieland';
  onNieuw: () => void;
}): JSX.Element {
  const titel = soort === '4dkl' ? '4DKL+ aanvraag' : 'Therapieland aanvraag';

  return (
    <>
      <div className="mt-6 rounded bg-white px-8 py-7" style={{ border: `1px solid ${KS.line}` }}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full"
              style={{ backgroundColor: '#eef1fb', color: KS.blue }}
            >
              <ClipboardPlusIcon size={19} />
            </span>
            <h2 className="text-[19px] font-bold" style={{ color: KS.ink }}>
              {titel}
            </h2>
            <span
              className="flex items-center gap-1.5 rounded px-2.5 py-1 text-[13px] font-bold"
              style={{ backgroundColor: KS.panel, color: KS.ink }}
            >
              <HourglassIcon size={14} /> Bij patiënt
            </span>
            <span className="text-[13.5px]" style={{ color: KS.muted }}>
              PID {patient.pid}
            </span>
          </div>
          <div className="flex items-start gap-6">
            <div className="text-right leading-tight">
              <p className="text-[13.5px]" style={{ color: KS.muted }}>
                T. Testarts
              </p>
              <p className="text-[13.5px]" style={{ color: KS.muted }}>
                Huisartsenpraktijk …
              </p>
            </div>
            <div className="text-right leading-tight">
              <p className="text-[14px] font-bold" style={{ color: KS.ink }}>
                16 jul
              </p>
              <p className="text-[14px] font-bold" style={{ color: KS.ink }}>
                2026
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <FormRij label="Huisarts">
            <span className="flex items-center gap-3">
              <span
                className="flex h-8 w-8 items-center justify-center rounded-full"
                style={{ backgroundColor: KS.panel, color: KS.muted }}
              >
                <StethoscopeIcon size={16} />
              </span>
              <span className="text-[15px] font-bold" style={{ color: KS.body }}>
                D. Demo - Huisartsenpraktijk Demo (Demostad)
              </span>
            </span>
          </FormRij>
          <FormRij label="E-mailadres patiënt">
            <span className="flex flex-wrap items-center gap-6">
              <span className="text-[15px]" style={{ color: KS.body }}>
                {patient.email}
              </span>
              <span className="text-[15px] font-bold" style={{ color: KS.blue }}>
                Verstuur naar ander e-mailadres
              </span>
            </span>
          </FormRij>
          <FormRij label="Verstuur naar">
            <span className="text-[15px] font-bold" style={{ color: KS.body }}>
              T. Testarts
            </span>
          </FormRij>
          <FormRij label="Nameting sturen">
            <span className="text-[15px] font-bold" style={{ color: KS.body }}>
              Nee
            </span>
          </FormRij>
        </div>

        <hr className="mt-6" style={{ borderColor: KS.line }} />
        <Vervolgstappen />

        <div className="mt-6 flex justify-end">
          <BlauweKnop onClick={onNieuw}>Print aanvraag</BlauweKnop>
        </div>
      </div>

      {/* Tweede kaart komt half in beeld, net als in het echte zorgpad. */}
      <div
        className="mt-5 flex items-center justify-between rounded-t bg-white px-8 pb-10 pt-6"
        style={{ border: `1px solid ${KS.line}`, borderBottom: 'none' }}
      >
        <div className="flex items-center gap-3">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-full"
            style={{ backgroundColor: '#eef1fb', color: KS.blue }}
          >
            <ClipboardPlusIcon size={19} />
          </span>
          <h2 className="text-[19px] font-bold" style={{ color: KS.ink }}>
            4DKL+
          </h2>
        </div>
        <p className="text-[13.5px]" style={{ color: KS.muted }}>
          {patient.naam}
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------- Taken en Patiëntenlijst --- */

type TaakBucket = 'mijn' | 'collega' | 'behandeling' | 'afgerond';
type TaakStatus = 'nieuw' | 'afgewezen' | 'behandeling' | 'afgerond';

interface Taak {
  bucket: TaakBucket;
  status: TaakStatus;
  patient: string;
  type: string;
  gestartDoor: [string, string];
  huisarts: [string, string];
  gewijzigd: string;
}

const EIGEN: [string, string] = ['T. Testarts', 'Huisartsenpraktijk Demo'];
const EIGEN_HA: [string, string] = ['D. Demo', 'Huisartsenpraktijk Demo'];

const TAKEN: Taak[] = [
  {
    bucket: 'mijn',
    status: 'nieuw',
    patient: 'proef',
    type: 'Face to face consult',
    gestartDoor: EIGEN,
    huisarts: EIGEN_HA,
    gewijzigd: '24-6-2026',
  },
  {
    bucket: 'mijn',
    status: 'nieuw',
    patient: 'test',
    type: 'Telefonisch consult',
    gestartDoor: EIGEN,
    huisarts: EIGEN_HA,
    gewijzigd: '24-6-2026',
  },
  {
    bucket: 'mijn',
    status: 'afgewezen',
    patient: 'kees',
    type: 'Verwijzing',
    gestartDoor: EIGEN,
    huisarts: ['R. Voorbeeld', 'Huisartsen Demoloo'],
    gewijzigd: '30-7-2018',
  },
  {
    bucket: 'collega',
    status: 'nieuw',
    patient: 'voorbeeld',
    type: 'Face to face consult',
    gestartDoor: ['K. Collega', 'Huisartsenpraktijk Demo'],
    huisarts: EIGEN_HA,
    gewijzigd: '23-6-2026',
  },
  {
    bucket: 'behandeling',
    status: 'behandeling',
    patient: 'proef',
    type: '4DKL+ aanvraag',
    gestartDoor: EIGEN,
    huisarts: EIGEN_HA,
    gewijzigd: '16-7-2026',
  },
  {
    bucket: 'afgerond',
    status: 'afgerond',
    patient: 'demo',
    type: 'Telefonisch consult',
    gestartDoor: EIGEN,
    huisarts: EIGEN_HA,
    gewijzigd: '12-6-2026',
  },
];

const TAAK_TABS: { key: TaakBucket; label: string }[] = [
  { key: 'mijn', label: 'Mijn taken' },
  { key: 'collega', label: 'Bij collega' },
  { key: 'behandeling', label: 'In behandeling' },
  { key: 'afgerond', label: 'Afgeronde taken' },
];

const STATUS_LABEL: Record<TaakStatus, string> = {
  nieuw: 'Nieuw consult',
  afgewezen: 'Afgewezen',
  behandeling: 'In behandeling',
  afgerond: 'Afgerond',
};

// Kolombreedtes overgenomen uit de screenshot (1728px inhoudsbreedte).
const TAAK_KOLOMMEN = '13% 15% 11% 15% 16% 16% 14%';

function TakenScherm({ onPatient }: { onPatient: (p: Patient) => void }): JSX.Element {
  const [tab, setTab] = useState<TaakBucket>('mijn');
  const [zoek, setZoek] = useState('');

  const rijen = TAKEN.filter((t) => {
    if (t.bucket !== tab) return false;
    if (!zoek) return true;
    const p = PATIENTEN.find((x) => x.id === t.patient)!;
    const hooi = [p.naam, p.gebdatum, p.pid, t.type, t.gestartDoor[0], t.huisarts[0]]
      .join(' ')
      .toLowerCase();
    return hooi.includes(zoek.toLowerCase());
  });

  return (
    <div className="w-full px-10 py-8">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <h1 className="shrink-0 text-[30px] font-bold" style={{ color: KS.ink }}>
          Ga aan de slag met
        </h1>
        <span
          className="flex min-w-[420px] max-w-[860px] flex-1 items-center gap-3 rounded-md bg-white px-4 py-2.5"
          style={{ border: `1px solid ${KS.muted}` }}
        >
          <input
            type="text"
            value={zoek}
            onChange={(e) => setZoek(e.target.value)}
            placeholder="Zoek een taak op geboortedatum, patiëntnaam, zorgverlener of proces ID…"
            className="w-full bg-transparent text-[14.5px] outline-none"
            style={{ color: KS.body }}
          />
          <SearchIcon size={19} style={{ color: KS.ink }} />
        </span>
      </div>

      <div className="mt-7 flex flex-wrap items-end justify-between gap-6">
        <div className="flex flex-wrap gap-3">
          {TAAK_TABS.map((t) => {
            const on = tab === t.key;
            return (
              <span key={t.key} className="relative">
                <button
                  type="button"
                  onClick={() => setTab(t.key)}
                  className="rounded-md px-[18px] py-2.5 text-[15px] font-bold"
                  style={{
                    border: `1.5px solid ${KS.blue}`,
                    backgroundColor: on ? KS.blue : 'transparent',
                    color: on ? '#fff' : KS.blue,
                  }}
                >
                  {t.label}
                </button>
                {/* Tuit onder de actieve knop, zoals in het portaal. */}
                {on && (
                  <span
                    className="absolute left-1/2 top-full h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rotate-45"
                    style={{ backgroundColor: KS.blue }}
                  />
                )}
              </span>
            );
          })}
        </div>

        <span
          className="flex w-[420px] items-center justify-between pb-1.5"
          style={{ borderBottom: `1px solid ${KS.border}` }}
        >
          <span className="text-[15px]" style={{ color: KS.muted }}>
            Filter op dienst…
          </span>
          <span className="flex items-center gap-3">
            <span className="h-5 w-px" style={{ backgroundColor: KS.border }} />
            <ChevronDownIcon size={18} style={{ color: KS.muted }} />
          </span>
        </span>
      </div>

      <div
        className="mt-8 grid items-center gap-3 rounded-md px-5 py-4"
        style={{ gridTemplateColumns: TAAK_KOLOMMEN, backgroundColor: KS.panel }}
      >
        {['Status', 'Patiënt', 'Geboortedatum', 'Type', 'Gestart door', 'Huisarts'].map((k) => (
          <span
            key={k}
            className="whitespace-nowrap text-[15px] font-bold"
            style={{ color: KS.ink }}
          >
            {k}
          </span>
        ))}
        <span
          className="flex items-center gap-2 whitespace-nowrap text-[15px] font-bold"
          style={{ color: KS.ink }}
        >
          Laatste wijziging <ArrowDownIcon size={16} />
        </span>
      </div>

      <div className="mt-2 space-y-2">
        {rijen.map((t, i) => {
          const p = PATIENTEN.find((x) => x.id === t.patient)!;
          const nieuw = t.status === 'nieuw';
          return (
            <button
              key={`${t.bucket}-${i}`}
              type="button"
              onClick={() => onPatient(p)}
              className="grid w-full items-center gap-3 rounded-md bg-white px-5 py-6 text-left hover:bg-[#faf6f8]"
              style={{ gridTemplateColumns: TAAK_KOLOMMEN, border: `1px solid ${KS.line}` }}
            >
              <span
                className="flex items-center gap-2.5 whitespace-nowrap text-[15px]"
                style={{ color: nieuw ? KS.pink : KS.body, fontWeight: nieuw ? 700 : 400 }}
              >
                {nieuw ? (
                  <FlagIcon size={17} className="shrink-0" />
                ) : (
                  <CheckIcon size={17} className="shrink-0" />
                )}
                {STATUS_LABEL[t.status]}
              </span>
              <span className="whitespace-nowrap text-[15px]" style={{ color: KS.body }}>
                {p.naam}
              </span>
              <span className="whitespace-nowrap text-[15px]" style={{ color: KS.body }}>
                {p.gebdatum}
              </span>
              <span
                className="flex items-center gap-2.5 whitespace-nowrap text-[15px]"
                style={{ color: KS.body }}
              >
                <BrainIcon size={18} strokeWidth={1.6} className="shrink-0" /> {t.type}
              </span>
              <TweeRegels regels={t.gestartDoor} />
              <TweeRegels regels={t.huisarts} />
              <span className="text-[15px]" style={{ color: KS.body }}>
                {t.gewijzigd}
              </span>
            </button>
          );
        })}
        {rijen.length === 0 && (
          <p className="bg-white px-5 py-6 text-[15px]" style={{ color: KS.muted }}>
            Geen taken in dit overzicht.
          </p>
        )}
      </div>
    </div>
  );
}

function TweeRegels({ regels }: { regels: [string, string] }): JSX.Element {
  return (
    <span className="leading-tight">
      <span className="block text-[15px]" style={{ color: KS.body }}>
        {regels[0]}
      </span>
      <span className="block text-[14px]" style={{ color: KS.muted }}>
        {regels[1]}
      </span>
    </span>
  );
}

// Patiëntenlijst; demogegevens, BSN-reeks bewust herkenbaar nep (99999xxxx).
const PATIENT_RIJEN = [
  { id: 'voorbeeld', gewijzigd: '16-7-2026' },
  { id: 'demo', gewijzigd: '13-10-2021' },
  { id: 'proef', gewijzigd: '2-7-2026' },
  { id: 'test', gewijzigd: '24-6-2026' },
  { id: 'kees', gewijzigd: '30-7-2026' },
];

// Kolombreedtes overgenomen uit de screenshot (1616px inhoudsbreedte).
const PATIENT_KOLOMMEN = '31% 10% 10% 17% 13% 19%';

function PatientenScherm({ onPatient }: { onPatient: (p: Patient) => void }): JSX.Element {
  const [alleenEigen, setAlleenEigen] = useState(true);

  return (
    <div className="w-full px-10 py-8">
      <h1 className="text-[30px] font-bold" style={{ color: KS.ink }}>
        Patiënten
      </h1>

      <div
        className="mt-6 rounded-2xl bg-white px-8 py-6"
        style={{ border: `1px solid ${KS.border}` }}
      >
        <p className="text-[19px] font-bold" style={{ color: KS.ink }}>
          Zoek patiënt
        </p>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <Zoekveld label="Naam" breed="w-[235px]" />
          <Zoekveld label="BSN" breed="w-[235px]" />
          <label className="block">
            <span className="block text-[14px]" style={{ color: KS.body }}>
              Geboortedatum
            </span>
            <span
              className="mt-1.5 flex w-[165px] items-center justify-between rounded border bg-white px-3 py-2.5 text-[14px]"
              style={{ borderColor: KS.inputBorder, color: KS.muted }}
            >
              dd-mm-jjjj <CalendarIcon size={16} style={{ color: KS.pink }} />
            </span>
          </label>
          <Zoekveld label="Procesnummer" breed="w-[235px]" />
          <button
            type="button"
            className="rounded-md px-[22px] py-2.5 text-[15px] font-bold"
            style={{ border: `1.5px solid ${KS.pink}`, color: KS.pink }}
          >
            Zoek
          </button>
          <span
            className="flex items-center gap-1.5 pb-1 text-[15px] font-bold"
            style={{ color: KS.pink }}
          >
            <PlusIcon size={17} /> Nieuwe patiënt
          </span>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-baseline gap-5">
        <h2 className="text-[24px] font-bold" style={{ color: KS.ink }}>
          Patiënten in jouw organisatie
        </h2>
        <span className="text-[15px] font-bold underline" style={{ color: KS.blue }}>
          Ga naar de privacy-stand
        </span>
      </div>

      <div className="mt-6 flex items-center justify-end gap-4">
        <span className="text-[15px]" style={{ color: KS.body }}>
          Toon alleen mijn patiënten
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={alleenEigen}
          onClick={() => setAlleenEigen(!alleenEigen)}
          className="flex h-[30px] w-[58px] items-center rounded-full px-1 transition-colors"
          style={{ backgroundColor: alleenEigen ? KS.blue : KS.border }}
        >
          <span
            className="h-[22px] w-[22px] rounded-full bg-white transition-transform"
            style={{ transform: alleenEigen ? 'translateX(26px)' : 'translateX(0)' }}
          />
        </button>
      </div>

      <div
        className="mt-3 grid items-center gap-3 rounded-md px-5 py-4"
        style={{ gridTemplateColumns: PATIENT_KOLOMMEN, backgroundColor: KS.panel }}
      >
        <span
          className="flex items-center gap-2 whitespace-nowrap text-[15px] font-bold"
          style={{ color: KS.ink }}
        >
          Naam <ArrowUpIcon size={16} />
        </span>
        {['Geslacht', 'BSN', 'Geboortedatum', 'Polisnummer', 'Laatste wijziging'].map((k) => (
          <span
            key={k}
            className="whitespace-nowrap text-[15px] font-bold"
            style={{ color: KS.ink }}
          >
            {k}
          </span>
        ))}
      </div>

      <div className="mt-2 space-y-2">
        {PATIENT_RIJEN.map((r) => {
          const p = PATIENTEN.find((x) => x.id === r.id)!;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => onPatient(p)}
              className="grid w-full items-center gap-3 rounded-md bg-white px-5 py-5 text-left hover:bg-[#faf6f8]"
              style={{ gridTemplateColumns: PATIENT_KOLOMMEN, border: `1px solid ${KS.line}` }}
            >
              <span
                className="flex items-center gap-3 whitespace-nowrap text-[15px]"
                style={{ color: KS.body }}
              >
                <UserIcon size={18} strokeWidth={1.5} className="shrink-0" /> {p.naam}
              </span>
              <span className="text-[15px]" style={{ color: KS.body }}>
                {p.geslacht}
              </span>
              <span className="text-[15px]" style={{ color: KS.body }}>
                {p.bsn}
              </span>
              <span className="text-[15px]" style={{ color: KS.body }}>
                {p.gebdatum}
              </span>
              <span className="text-[15px]" style={{ color: KS.body }}>
                {p.polis}
              </span>
              <span className="text-[15px]" style={{ color: KS.body }}>
                {r.gewijzigd}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Inloggen --- */

/** Nabootsing van connect.ksyos.com/login. */
function KsyosLogin({ onLogin }: { onLogin: () => void }): JSX.Element {
  const KS_INK = '#312d2c';
  const veldClass =
    'mt-1.5 w-full rounded-md border border-[#c9c5c3] bg-white px-3 py-2.5 text-[14px] outline-none focus:border-[color:var(--brand)]';
  const vragen = [
    'Vraag zelf een account aan',
    'Eenvoudig inloggen via je HIS',
    'Veelgestelde vragen',
  ];

  return (
    <div className="grid min-h-0 flex-1 gap-6 overflow-y-auto bg-white p-6 lg:grid-cols-[minmax(320px,30%)_1fr]">
      <div className="flex flex-col">
        <img src="/ksyos-logo.svg" alt="Ksyos" className="h-8 w-auto self-start" />

        <h3 className="mt-8 text-[30px] font-bold leading-tight" style={{ color: KS_INK }}>
          Log in als
          <br />
          zorgprofessional
        </h3>

        <div className="mt-7">
          <label className="block">
            <span className="text-[14px]" style={{ color: KS_INK }}>
              Gebruikersnaam
            </span>
            <input type="text" defaultValue="t.testarts" className={veldClass} />
          </label>
          <span className="mt-1.5 inline-block text-[13px] underline" style={{ color: KS_INK }}>
            Gebruikersnaam vergeten
          </span>
        </div>

        <div className="mt-4">
          <label className="block">
            <span className="text-[14px]" style={{ color: KS_INK }}>
              Wachtwoord
            </span>
            <span className="relative mt-1.5 block">
              <input
                type="password"
                defaultValue="demodemo"
                className={`${veldClass} mt-0 pr-10`}
              />
              <EyeOffIcon
                size={17}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: KS_INK }}
              />
            </span>
          </label>
          <span className="mt-1.5 inline-block text-[13px] underline" style={{ color: KS_INK }}>
            Wachtwoord vergeten
          </span>
        </div>

        <button
          type="button"
          onClick={onLogin}
          className="mt-6 w-full rounded-md py-3 text-[15px] font-semibold text-white"
          style={{ backgroundColor: KS.pink }}
        >
          Log in
        </button>

        <span className="mt-5 self-center text-[13.5px] underline" style={{ color: KS_INK }}>
          Ik moet mijn account nog activeren
        </span>

        <div className="mt-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-[#d8d4d2]" />
          <span className="text-[13.5px]" style={{ color: KS_INK }}>
            Nog vragen?
          </span>
          <span className="h-px flex-1 bg-[#d8d4d2]" />
        </div>

        <ul className="mt-5 space-y-4 text-center">
          {vragen.map((v) => (
            <li key={v} className="text-[14px] font-bold" style={{ color: KS.pink }}>
              {v}
            </li>
          ))}
        </ul>

        <p className="mt-auto pt-8 text-center text-[12px] leading-snug" style={{ color: KS_INK }}>
          Het Ksyos platform bevat persoonlijke gezondheidsinformatie. Deze informatie is
          vertrouwelijk.
        </p>
      </div>

      <div className="relative min-h-[420px] overflow-hidden rounded-lg">
        <img
          src="/login-achtergornd-ksyos.webp"
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
          style={{ filter: 'grayscale(1) brightness(1.6) contrast(0.7)' }}
        />
        <div
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(135deg, #ffc37a 0%, #ffb0c8 45%, #ff9fd6 100%)',
            mixBlendMode: 'multiply',
          }}
        />
        <div className="absolute bottom-8 right-0 max-w-[62%] bg-white px-10 py-8">
          <img src="/ksyos-logo.svg" alt="Ksyos" className="h-12 w-auto" />
          <p className="mt-3 text-[19px] font-semibold" style={{ color: KS_INK }}>
            Maakt de zorg slimmer, beter en menselijker
          </p>
        </div>
      </div>
    </div>
  );
}
